import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Dumbbell, Target, Zap, TrendingUp, ChevronRight, PlayCircle, Camera, Activity, ChefHat, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { VideoBackground } from '../components/VideoBackground';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Landing = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showDemos, setShowDemos] = useState(false);
  const [suggestedGoalWeight, setSuggestedGoalWeight] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    gender: '',
    age: '',
    height: '',
    current_weight: '',
    goal_weight: '',
    goal: 'fat_loss',
  });

  const calculateHealthyWeight = () => {
    if (formData.height && formData.current_weight) {
      const heightM = parseFloat(formData.height) / 100;
      const currentBMI = parseFloat(formData.current_weight) / (heightM * heightM);
      
      // Healthy BMI range: 18.5 - 24.9
      // Calculate weight for BMI 22 (middle of healthy range)
      const idealBMI = 22;
      const idealWeight = idealBMI * (heightM * heightM);
      
      setSuggestedGoalWeight({
        weight: Math.round(idealWeight * 10) / 10,
        currentBMI: Math.round(currentBMI * 10) / 10,
        category: currentBMI < 18.5 ? 'Underweight' : currentBMI < 25 ? 'Normal' : currentBMI < 30 ? 'Overweight' : 'Obese'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            gender: formData.gender || undefined,
            age: parseInt(formData.age) || undefined,
            height: parseFloat(formData.height) || undefined,
            current_weight: parseFloat(formData.current_weight) || undefined,
            goal_weight: parseFloat(formData.goal_weight) || undefined,
            goal: formData.goal,
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      const { token, user_id, name } = response.data;

      login(token, { id: user_id, name, email: formData.email });
      toast.success(`Welcome ${name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const demoVideos = [
    {
      id: 1,
      title: 'AI Food Recognition Demo',
      description: 'See how AI instantly analyzes your food and provides complete nutritional breakdown',
      icon: Camera,
      embed_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=600',
      color: 'primary'
    },
    {
      id: 2,
      title: 'Smart Workout Tracking',
      description: 'Log exercises, track progress, and access professional workout programs',
      icon: Dumbbell,
      embed_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=600',
      color: 'chart-2'
    },
    {
      id: 3,
      title: 'AI Diet Coach',
      description: 'Get personalized meal plans generated by AI based on your goals and preferences',
      icon: ChefHat,
      embed_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.unsplash.com/photo-1622637103261-ae624e188bd0?w=600',
      color: 'chart-3'
    },
    {
      id: 4,
      title: 'Progress Analytics',
      description: 'Visualize your transformation with detailed charts and AI-powered insights',
      icon: BarChart3,
      embed_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600',
      color: 'chart-4'
    },
  ];

  const [playingVideo, setPlayingVideo] = useState(null);

  if (showDemos) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Button
              onClick={() => {
                setShowDemos(false);
                setPlayingVideo(null);
              }}
              data-testid="back-to-home"
              variant="outline"
              className="mb-6 uppercase font-bold tracking-wider"
            >
              ← Back to Home
            </Button>
            <h1 className="text-5xl font-barlow font-black uppercase mb-4">Feature Demos</h1>
            <p className="text-muted-foreground uppercase text-xs tracking-widest">See FitTrack AI in Action</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {demoVideos.map((demo, index) => (
              <motion.div
                key={demo.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`demo-video-${demo.id}`}
                className="bg-card border border-border hover:border-primary/50 transition-colors overflow-hidden group"
              >
                {playingVideo === demo.id ? (
                  <div className="relative h-64 bg-black">
                    <iframe
                      src={demo.embed_url}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={demo.title}
                    />
                  </div>
                ) : (
                  <div className="relative h-64 overflow-hidden bg-secondary cursor-pointer" onClick={() => setPlayingVideo(demo.id)}>
                    <img
                      src={demo.thumbnail}
                      alt={demo.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/70 transition-colors flex items-center justify-center">
                      <PlayCircle className="w-20 h-20 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                      <demo.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-barlow font-black uppercase">{demo.title}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">{demo.description}</p>
                  <Button
                    onClick={() => setPlayingVideo(demo.id)}
                    data-testid={`watch-demo-${demo.id}`}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 uppercase font-bold tracking-widest"
                  >
                    <PlayCircle className="mr-2 w-5 h-5" />
                    {playingVideo === demo.id ? 'Playing...' : 'Watch Demo'}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (showAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md glass-card rounded-2xl p-5 sm:p-8 mx-4 sm:mx-0 max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/40"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-barlow font-black text-primary uppercase mb-2">
              {isLogin ? 'Welcome Back' : 'Start Your Journey'}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isLogin ? 'Login to continue your fitness journey' : 'Create your account and transform'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="uppercase text-xs tracking-wider">Full Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="mt-1 bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary/50 h-12 text-base"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="uppercase text-xs tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary/50 h-12 text-base"
              />
            </div>

            <div>
              <Label htmlFor="password" className="uppercase text-xs tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1 bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary/50 h-12 text-base"
              />
            </div>

            {!isLogin && (
              <>
                {/* Gender Selection */}
                <div>
                  <Label className="uppercase text-xs tracking-wider mb-2 block">Gender</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'male', label: 'Male', icon: '♂', color: 'from-blue-500 to-blue-600' },
                      { value: 'female', label: 'Female', icon: '♀', color: 'from-pink-500 to-pink-600' },
                      { value: 'other', label: 'Other', icon: '⚧', color: 'from-purple-500 to-purple-600' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        data-testid={`register-gender-${option.value}`}
                        onClick={() => setFormData({ ...formData, gender: option.value })}
                        className={`relative flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all duration-200 ${
                          formData.gender === option.value
                            ? 'border-primary bg-primary/10 scale-[1.02] shadow-md'
                            : 'border-border hover:border-primary/40 hover:bg-primary/5'
                        }`}
                      >
                        <span className={`text-2xl ${
                          formData.gender === option.value ? '' : 'opacity-60'
                        }`}>{option.icon}</span>
                        <span className={`text-xs font-bold uppercase tracking-wider ${
                          formData.gender === option.value ? 'text-primary' : 'text-muted-foreground'
                        }`}>{option.label}</span>
                        {formData.gender === option.value && (
                          <motion.div
                            layoutId="gender-indicator"
                            className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center"
                            initial={false}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          >
                            <span className="text-[8px] text-primary-foreground font-bold">✓</span>
                          </motion.div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="age" className="uppercase text-xs tracking-wider">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      data-testid="register-age-input"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="mt-1 bg-input/50 border-input h-12 text-base"
                    />
                  </div>
                  <div>
                    <Label htmlFor="height" className="uppercase text-xs tracking-wider">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      data-testid="register-height-input"
                      value={formData.height}
                      onChange={(e) => {
                        setFormData({ ...formData, height: e.target.value });
                        setSuggestedGoalWeight(null);
                      }}
                      className="mt-1 bg-input/50 border-input h-12 text-base"
                    />
                  </div>
                  <div>
                    <Label htmlFor="current_weight" className="uppercase text-xs tracking-wider">Current Weight (kg)</Label>
                    <Input
                      id="current_weight"
                      type="number"
                      step="0.1"
                      data-testid="register-weight-input"
                      value={formData.current_weight}
                      onChange={(e) => {
                        setFormData({ ...formData, current_weight: e.target.value });
                        setSuggestedGoalWeight(null);
                      }}
                      className="mt-1 bg-input/50 border-input h-12 text-base"
                    />
                  </div>
                  <div>
                    <Label htmlFor="goal_weight" className="uppercase text-xs tracking-wider">Goal Weight (kg)</Label>
                    <Input
                      id="goal_weight"
                      type="number"
                      step="0.1"
                      data-testid="register-goal-weight-input"
                      value={formData.goal_weight}
                      onChange={(e) => setFormData({ ...formData, goal_weight: e.target.value })}
                      placeholder="Set manually or use BMI"
                      className="mt-1 bg-input/50 border-input h-12 text-base"
                    />
                  </div>
                </div>

                {formData.height && formData.current_weight && (
                  <div className="space-y-3">
                    <Button
                      type="button"
                      onClick={calculateHealthyWeight}
                      data-testid="calculate-healthy-weight-button"
                      variant="outline"
                      className="w-full border-primary/50 hover:bg-primary/10 text-xs uppercase font-bold tracking-wider"
                    >
                      <Target className="w-4 h-4 mr-2" />
                      Calculate Healthy Goal Weight (BMI)
                    </Button>

                    {suggestedGoalWeight && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-primary/10 border border-primary/30 p-4 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs uppercase tracking-wider text-muted-foreground">Your Current BMI</span>
                          <span className="font-bold text-foreground">{suggestedGoalWeight.currentBMI} ({suggestedGoalWeight.category})</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs uppercase tracking-wider text-muted-foreground">Suggested Goal Weight</span>
                          <span className="font-bold text-primary text-lg">{suggestedGoalWeight.weight} kg</span>
                        </div>
                        <Button
                          type="button"
                          onClick={() => setFormData({ ...formData, goal_weight: suggestedGoalWeight.weight.toString() })}
                          data-testid="use-suggested-weight-button"
                          size="sm"
                          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-8 text-xs uppercase font-bold tracking-wider"
                        >
                          Use This Goal
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          Based on healthy BMI range (18.5-24.9). You can still set your own goal above.
                        </p>
                      </motion.div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="goal" className="uppercase text-xs tracking-wider">Fitness Goal</Label>
                  <Select
                    value={formData.goal}
                    onValueChange={(value) => setFormData({ ...formData, goal: value })}
                  >
                    <SelectTrigger data-testid="register-goal-select" className="mt-1 h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fat_loss">Fat Loss</SelectItem>
                      <SelectItem value="muscle_gain">Muscle Gain</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="athlete">Athlete Mode</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-orange-600 text-primary-foreground hover:opacity-90 h-12 uppercase font-bold tracking-widest text-sm rounded-lg shadow-lg shadow-primary/20 transition-all"
            >
              {loading ? 'Processing...' : isLogin ? 'Login' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <VideoBackground />

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-barlow font-black text-foreground uppercase tracking-tight mb-4 sm:mb-6">
              AI-Powered
              <span className="block text-primary mt-1 sm:mt-2 text-glow">Fitness Revolution</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8 font-manrope px-4">
              Track food with AI vision. Get personalized diet plans. Achieve your fitness goals with precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Button
                onClick={() => setShowAuth(true)}
                data-testid="get-started-button"
                className="bg-gradient-to-r from-primary to-orange-600 text-primary-foreground hover:opacity-90 h-12 sm:h-14 px-8 sm:px-12 uppercase font-bold tracking-widest text-sm sm:text-base rounded-lg shadow-xl shadow-primary/25 transition-all hover:shadow-2xl hover:shadow-primary/30 w-full sm:w-auto"
              >
                Start Your Journey
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => setShowDemos(true)}
                data-testid="view-demos-button"
                variant="outline"
                className="border-primary/40 text-primary hover:bg-primary/10 h-12 sm:h-14 px-8 sm:px-12 uppercase font-bold tracking-widest text-sm sm:text-base rounded-lg backdrop-blur-sm w-full sm:w-auto"
              >
                <PlayCircle className="mr-2 w-5 h-5" />
                Watch Demos
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-card/50 border-y border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
          >
            <div className="p-6 sm:p-8 glass-card rounded-xl hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-barlow font-black uppercase mb-3 sm:mb-4">AI Food Recognition</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Snap a photo, get instant nutritional breakdown. No manual logging needed.</p>
            </div>

            <div className="p-6 sm:p-8 glass-card rounded-xl hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-barlow font-black uppercase mb-3 sm:mb-4">Smart Diet Coach</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Personalized meal plans that adapt to your progress and goals.</p>
            </div>

            <div className="p-6 sm:p-8 glass-card rounded-xl hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 sm:col-span-2 md:col-span-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-barlow font-black uppercase mb-3 sm:mb-4">Progress Analytics</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Track every metric. Visualize your transformation journey.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-barlow font-black uppercase mb-4 sm:mb-6">Ready to Transform?</h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-xl mx-auto px-4">
              Join thousands who are achieving their fitness goals with AI-powered precision.
            </p>
            <Button
              onClick={() => setShowAuth(true)}
              data-testid="cta-get-started-button"
              className="bg-gradient-to-r from-primary to-orange-600 text-primary-foreground hover:opacity-90 h-12 sm:h-14 px-8 sm:px-12 uppercase font-bold tracking-widest text-sm sm:text-base rounded-lg shadow-xl shadow-primary/25 w-full sm:w-auto"
            >
              Get Started Free
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
